import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { ref, onValue, update, get } from 'firebase/database';
import { Shield, UserX, UserCheck, Search, Users, AlertTriangle, CheckCircle, X, ArrowLeft, ShieldCheck, CheckCheck, Send, User, Mail, Phone, Calendar, Building2, MapPin, Eye, ExternalLink, Bed, Activity, ChevronLeft, ChevronRight, Database } from 'lucide-react';
import { decryptText } from '../utils/encryption';
import { format, isToday, isThisYear } from 'date-fns';
import AdminCMS from './AdminCMS';
import HistoricalDataImport from './HistoricalDataImport';

const AdminDashboard = ({ profile, uid }) => {
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userPage, setUserPage] = useState(1);
  const [activeTab, setActiveTab] = useState('users');
  const [selectedUser, setSelectedUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [previewImage, setPreviewImage] = useState(null); // { url, title }

  // Ban modal state
  const [banModal, setBanModal] = useState(null); // { user, action: 'ban'|'unban' }
  const [banReason, setBanReason] = useState('');
  const [banError, setBanError] = useState('');
  const [banLoading, setBanLoading] = useState(false);

  // Resolve modal state
  const [resolveModal, setResolveModal] = useState(null); // report object
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveAction, setResolveAction] = useState('dismiss');
  const [resolveMessage, setResolveMessage] = useState('');
  const [reporterPhoto, setReporterPhoto] = useState(null);
  const [reportedPhoto, setReportedPhoto] = useState(null);

  useEffect(() => {
    if (!selectedReport || !chatOpen) return;
    setChatLoading(true);

    const fetchPhotos = async () => {
      try {
        const reporterPropSnap = await get(ref(db, `properties/${selectedReport.reporterUid}`));
        if (reporterPropSnap.exists()) {
          const propData = reporterPropSnap.val();
          const imgs = Array.isArray(propData.imageUrls) ? propData.imageUrls : (propData.imageUrls ? Object.values(propData.imageUrls) : []);
          if (imgs.length > 0) setReporterPhoto(imgs[0]);
        } else {
          const userSnap = await get(ref(db, `users/${selectedReport.reporterUid}`));
          if (userSnap.exists() && userSnap.val().profilePicUrl) {
            setReporterPhoto(userSnap.val().profilePicUrl);
          } else {
            setReporterPhoto(null);
          }
        }
      } catch (e) { console.warn("Failed fetching reporter photo", e); }

      try {
        const reportedPropSnap = await get(ref(db, `properties/${selectedReport.reportedUid}`));
        if (reportedPropSnap.exists()) {
          const propData = reportedPropSnap.val();
          const imgs = Array.isArray(propData.imageUrls) ? propData.imageUrls : (propData.imageUrls ? Object.values(propData.imageUrls) : []);
          if (imgs.length > 0) setReportedPhoto(imgs[0]);
        } else {
          const userSnap = await get(ref(db, `users/${selectedReport.reportedUid}`));
          if (userSnap.exists() && userSnap.val().profilePicUrl) {
            setReportedPhoto(userSnap.val().profilePicUrl);
          } else {
            setReportedPhoto(null);
          }
        }
      } catch (e) { console.warn("Failed fetching reported photo", e); }
    };
    fetchPhotos();

    const sortedIds = [selectedReport.reportedUid, selectedReport.reporterUid].sort();
    const chatId = sortedIds.join('_');
    const chatRef = ref(db, `chats/${chatId}/messages`);
    get(chatRef)
      .then(snap => {
        const data = snap.val();
        if (data) {
          const msgs = Object.entries(data)
            .map(([msgId, val]) => {
              let decrypted = '';
              try {
                decrypted = decryptText(val.text, chatId);
              } catch (e) {
                console.error('Failed to decrypt message:', e);
                decrypted = '[Could not decrypt message]';
              }
              return { id: msgId, ...val, decryptedText: decrypted || val.text };
            })
            .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
          setChatMessages(msgs);
        } else {
          setChatMessages([]);
        }
        setChatLoading(false);
      })
      .catch(err => {
        console.error('Failed to load chat:', err);
        setChatLoading(false);
      });
  }, [chatOpen, selectedReport]);

  useEffect(() => {
    const usersRef = ref(db, 'users');
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.entries(data)
          .map(([id, val]) => ({ id, ...val }))
          .filter(u => u.id !== uid)
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setUsers(list);
      }
      setLoading(false);
    });

    const reportsRef = ref(db, 'reports');
    const unsubReports = onValue(reportsRef, (snap) => {
      const data = snap.val();
      if (data) {
        setReports(Object.entries(data).map(([id, val]) => ({ id, ...val })).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)));
      } else {
        setReports([]);
      }
    });

    const propsRef = ref(db, 'properties');
    const unsubProps = onValue(propsRef, (snap) => {
      const data = snap.val();
      if (data) {
        const list = Object.entries(data).map(([id, val]) => ({ id, ...val }));
        setProperties(list);
      } else {
        setProperties([]);
      }
    });

    return () => { unsubscribe(); unsubReports(); unsubProps(); };
  }, [uid]);

  const openBanModal = (user) => {
    setBanModal(user);
    setBanReason('');
    setBanError('');
  };

  const confirmBan = async () => {
    if (!banModal) return;
    const isBanning = !banModal.isBanned;
    if (isBanning && !banReason.trim()) {
      setBanError('Please provide a reason for restricting this user.');
      return;
    }
    setBanLoading(true);
    try {
      await update(ref(db, `users/${banModal.id}`), {
        isBanned: isBanning,
        ...(isBanning ? { banReason: banReason.trim(), bannedAt: Date.now() } : { banReason: null, bannedAt: null })
      });
      setBanModal(null);
      setBanReason('');
      setBanError('');
    } catch (e) {
      setBanError(`Failed: ${e.message}`);
    }
    setBanLoading(false);
  };

  const openResolveModal = (report) => {
    setResolveAction('dismiss');
    setResolveMessage('');
    setResolveModal(report);
  };

  const confirmResolve = async () => {
    if (!resolveModal) return;
    setResolveLoading(true);
    try {
      const updates = {};
      
      if (resolveAction === 'ban_reported') {
        updates[`users/${resolveModal.reportedUid}/isBanned`] = true;
        updates[`users/${resolveModal.reportedUid}/banReason`] = resolveMessage || 'Banned due to report violation';
        updates[`users/${resolveModal.reportedUid}/bannedAt`] = Date.now();
      } else if (resolveAction === 'warn_reported') {
        const userSnap = await get(ref(db, `users/${resolveModal.reportedUid}/warningCount`));
        const currentWarnings = userSnap.exists() ? userSnap.val() : 0;
        const newWarnings = currentWarnings + 1;
        
        updates[`users/${resolveModal.reportedUid}/warningCount`] = newWarnings;
        
        if (newWarnings >= 3) {
          updates[`users/${resolveModal.reportedUid}/isBanned`] = true;
          updates[`users/${resolveModal.reportedUid}/banReason`] = 'Accumulated 3 Warnings';
          updates[`users/${resolveModal.reportedUid}/bannedAt`] = Date.now();
        }

        const notifKey = `notifications/${resolveModal.reportedUid}/${Date.now()}`;
        updates[notifKey] = {
          title: newWarnings >= 3 ? 'Account Banned' : 'Official Warning',
          message: newWarnings >= 3 ? 'Your account has been banned due to accumulating 3 warnings.' : (resolveMessage || 'You have received a warning regarding your recent conduct.'),
          type: 'warning',
          read: false,
          timestamp: Date.now()
        };
      } else if (resolveAction === 'warn_reporter') {
        const userSnap = await get(ref(db, `users/${resolveModal.reporterUid}/warningCount`));
        const currentWarnings = userSnap.exists() ? userSnap.val() : 0;
        const newWarnings = currentWarnings + 1;

        updates[`users/${resolveModal.reporterUid}/warningCount`] = newWarnings;

        if (newWarnings >= 3) {
          updates[`users/${resolveModal.reporterUid}/isBanned`] = true;
          updates[`users/${resolveModal.reporterUid}/banReason`] = 'Accumulated 3 Warnings';
          updates[`users/${resolveModal.reporterUid}/bannedAt`] = Date.now();
        }

        const notifKey = `notifications/${resolveModal.reporterUid}/${Date.now()}`;
        updates[notifKey] = {
          title: newWarnings >= 3 ? 'Account Banned' : 'Official Warning: False Report',
          message: newWarnings >= 3 ? 'Your account has been banned due to accumulating 3 warnings.' : (resolveMessage || 'You have received a warning for submitting a false or inappropriate report.'),
          type: 'warning',
          read: false,
          timestamp: Date.now()
        };
      }

      updates[`reports/${resolveModal.id}/status`] = 'resolved';
      updates[`reports/${resolveModal.id}/resolvedAt`] = Date.now();
      updates[`reports/${resolveModal.id}/resolveAction`] = resolveAction;

      await update(ref(db), updates);
      setResolveModal(null);
    } catch (e) {
      console.error(e);
      alert('Failed to resolve report: ' + e.message);
    }
    setResolveLoading(false);
  };

  const getReporterName = (uid) => {
    const user = users.find(u => u.id === uid);
    if (user) {
      const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      return name ? name : uid;
    }
    return uid;
  };

  const filteredUsers = users.filter(u => {
    if (!u) return false;
    const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
    const email = (u.email || '').toLowerCase();
    const query = (searchQuery || '').toLowerCase();
    const matchesSearch = fullName.includes(query) || email.includes(query);
    let matchesStatus = true;
    if (statusFilter === 'active') matchesStatus = !u.isBanned;
    if (statusFilter === 'suspended') matchesStatus = !!u.isBanned;
    if (statusFilter === 'owner') matchesStatus = u.role === 'Owner';
    if (statusFilter === 'tourist') matchesStatus = (u.role === 'Tourist' || !u.role);
    return matchesSearch && matchesStatus;
  });

  const USERS_PER_PAGE = 10;
  const totalUserPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const currentSafeUserPage = Math.min(userPage, totalUserPages);
  const paginatedUsers = filteredUsers.slice((currentSafeUserPage - 1) * USERS_PER_PAGE, currentSafeUserPage * USERS_PER_PAGE);

  const stats = {
    total: users.length,
    tourists: users.filter(u => u.role === 'Tourist' || !u.role).length,
    owners: users.filter(u => u.role === 'Owner').length,
  };

  const pendingReports = reports.filter(r => r.status === 'pending').length;

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <div className="loader"></div>
    </div>
  );

  const resortPartnersCount = properties.length > 0 ? properties.length : stats.owners;

  return (
    <div className="view-transition" style={{ width: '100%', margin: '0 auto', paddingBottom: '32px' }}>
      <div className="card admin-hero-banner" style={{
        background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
        color: 'white', marginBottom: '20px', padding: '24px 20px',
        border: 'none', position: 'relative', overflow: 'hidden',
        borderRadius: '20px'
      }}>
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '100%' }}>
          <h2 className="admin-hero-title" style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: 0, fontSize: '26px', fontWeight: 800 }}>
            <Shield size={30} style={{ flexShrink: 0 }} /> System Control
          </h2>
          <p className="admin-hero-subtitle" style={{ opacity: 0.9, margin: '8px 0 0 0', fontSize: '13.5px', fontWeight: 500, lineHeight: 1.5 }}>
            Oversee ecosystem health, manage memberships, and maintain security.
          </p>
        </div>
        <Shield size={160} style={{ position: 'absolute', right: '-30px', bottom: '-30px', opacity: 0.1, transform: 'rotate(-15deg)', pointerEvents: 'none' }} />
      </div>

      <div className="admin-stats-grid">
        <StatItem icon={<Users color="var(--secondary)" size={22} />} label="Total Users" value={stats.total} bgGradient="linear-gradient(135deg, rgba(29,211,176,0.15), rgba(29,211,176,0.05))" onClick={() => setActiveTab('users')} />
        <StatItem icon={<AlertTriangle color="#EF4444" size={22} />} label="Pending Reports" value={pendingReports} bgGradient="linear-gradient(135deg, rgba(239,68,68,0.15), rgba(239,68,68,0.05))" onClick={() => setActiveTab('reports')} />
        <StatItem icon={<Building2 color="#3B82F6" size={22} />} label="Resort Partners" value={resortPartnersCount} bgGradient="linear-gradient(135deg, rgba(59,130,246,0.15), rgba(59,130,246,0.05))" onClick={() => setActiveTab('partners')} />
      </div>

      <div className="admin-tabs-nav" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginBottom: '20px', borderBottom: '2px solid var(--border)', paddingBottom: '12px' }}>
        <button onClick={() => setActiveTab('users')} style={{ background: 'none', border: 'none', fontSize: '16px', fontWeight: 800, color: activeTab === 'users' ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer', transition: 'var(--transition)' }}>All Users</button>
        <button onClick={() => setActiveTab('partners')} style={{ background: 'none', border: 'none', fontSize: '16px', fontWeight: 800, color: activeTab === 'partners' ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer', transition: 'var(--transition)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          Resort Partners {resortPartnersCount > 0 && <span style={{ background: '#3B82F6', color: 'white', fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 800 }}>{resortPartnersCount}</span>}
        </button>
        <button onClick={() => setActiveTab('reports')} style={{ background: 'none', border: 'none', fontSize: '16px', fontWeight: 800, color: activeTab === 'reports' ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer', transition: 'var(--transition)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          Reports {pendingReports > 0 && <span style={{ background: '#EF4444', color: 'white', fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 800 }}>{pendingReports}</span>}
        </button>
        <button onClick={() => setActiveTab('cms')} style={{ background: 'none', border: 'none', fontSize: '16px', fontWeight: 800, color: activeTab === 'cms' ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer', transition: 'var(--transition)' }}>Landing Page</button>
        <button onClick={() => setActiveTab('import')} style={{ background: 'none', border: 'none', fontSize: '16px', fontWeight: 800, color: activeTab === 'import' ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer', transition: 'var(--transition)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Database size={16} /> Import Historical Data
        </button>
      </div>

      {activeTab === 'users' && (
        <>
          <div className="admin-filter-bar" style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
            <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '13.5px', lineHeight: 1.5, flex: '1 1 240px' }}>Review and manage user access permissions.</p>
            <div className="admin-filter-controls" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', flex: '2 1 320px', justifyContent: 'flex-end' }}>
              <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '160px', width: '100%' }}>
                <Search style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} size={16} />
                <input type="text" placeholder="Search by name or email..." className="input"
                  style={{ paddingLeft: '42px', height: '44px', borderRadius: '12px', width: '100%', fontSize: '13.5px' }}
                  value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setUserPage(1); }} />
              </div>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setUserPage(1); }} className="input"
                style={{ height: '44px', borderRadius: '12px', flex: '0 1 140px', minWidth: '130px', padding: '0 12px', background: 'var(--surface)', cursor: 'pointer', fontSize: '13.5px' }}>
                <option value="all">All Accounts</option>
                <option value="active">Active Only</option>
                <option value="suspended">Suspended</option>
                <option value="owner">Owners</option>
                <option value="tourist">Tourists</option>
              </select>
            </div>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', borderRadius: '20px' }}>
            <table className="admin-responsive-table admin-users-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
              <thead>
                <tr style={{ background: 'var(--light-bg)', borderBottom: '1px solid var(--border)', textAlign: 'left', height: '52px' }}>
                  <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Account</th>
                  <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Type</th>
                  <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Status / Reason</th>
                  <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 && (
                  <tr><td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '14px' }}>No users found.</td></tr>
                )}
                {paginatedUsers.map(user => (
                  <tr key={user.id} className="table-row admin-user-row">
                    <td data-label="Account" className="admin-cell-account" style={{ padding: '14px 18px', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '38px', height: '38px', borderRadius: '12px', flexShrink: 0,
                          background: user.isBanned ? 'rgba(239, 68, 68, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                          display: 'flex', justifyContent: 'center', alignItems: 'center',
                          color: user.isBanned ? '#EF4444' : '#2563EB', fontSize: '16px', fontWeight: 800
                        }}>
                          {user.firstName?.charAt(0) || 'U'}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 800, fontSize: '14.5px', color: 'var(--text-main)', wordBreak: 'break-word' }}>
                              {user.firstName} {user.lastName}
                            </span>
                            <span className="admin-user-type-badge" style={{
                              fontSize: '10px', padding: '2px 8px', borderRadius: '6px',
                              background: user.role === 'Owner' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(100, 116, 139, 0.12)',
                              color: user.role === 'Owner' ? '#10B981' : 'var(--text-muted)',
                              fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px', display: 'inline-block'
                            }}>
                              {user.role || 'Tourist'}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 500, wordBreak: 'break-all', marginTop: '1px' }}>
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td data-label="Type" className="admin-cell-type" style={{ padding: '14px 18px', wordBreak: 'break-word' }}>
                      <span style={{
                        fontSize: '11px', padding: '4px 10px', borderRadius: '8px',
                        background: user.role === 'Owner' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(100, 116, 139, 0.12)',
                        color: user.role === 'Owner' ? '#10B981' : 'var(--text-muted)',
                        fontWeight: 800, textTransform: 'uppercase', display: 'inline-block'
                      }}>
                        {user.role || 'Tourist'}
                      </span>
                    </td>
                    <td data-label="Status / Reason" className="admin-cell-status" style={{ padding: '14px 18px', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      {user.isBanned ? (
                        <div>
                          <div style={{ color: '#EF4444', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 700 }}>
                            <UserX size={14} /> Restricted
                          </div>
                          {user.banReason && (
                            <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px', lineHeight: 1.35, wordBreak: 'break-word' }}>
                              Reason: {user.banReason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ color: '#10B981', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 700 }}>
                          <UserCheck size={14} /> Active
                        </div>
                      )}
                    </td>
                    <td data-label="Actions" className="admin-actions-cell" style={{ padding: '14px 18px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="btn btn-secondary"
                          style={{ padding: '7px 13px', fontSize: '12px', borderRadius: '10px', fontWeight: 700 }}
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => openBanModal(user)}
                          className="btn"
                          style={{
                            display: 'inline-flex', padding: '7px 13px', fontSize: '12px', fontWeight: 700,
                            background: user.isBanned ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                            color: user.isBanned ? '#10B981' : '#EF4444',
                            borderRadius: '10px', border: 'none'
                          }}
                        >
                          {user.isBanned ? 'Unban Account' : 'Restrict Access'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredUsers.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 24px',
                borderTop: '1px solid var(--border)',
                background: 'var(--surface)',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 500 }}>
                  Showing <strong style={{ color: 'var(--text-main)' }}>{(currentSafeUserPage - 1) * USERS_PER_PAGE + 1}</strong> to <strong style={{ color: 'var(--text-main)' }}>{Math.min(currentSafeUserPage * USERS_PER_PAGE, filteredUsers.length)}</strong> of <strong style={{ color: 'var(--text-main)' }}>{filteredUsers.length}</strong> users
                </span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    disabled={currentSafeUserPage <= 1}
                    onClick={() => setUserPage(p => Math.max(1, p - 1))}
                    className="btn btn-secondary"
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      borderRadius: '8px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      opacity: currentSafeUserPage <= 1 ? 0.4 : 1,
                      cursor: currentSafeUserPage <= 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <ChevronLeft size={16} /> Previous
                  </button>
                  <span style={{ fontSize: '13px', fontWeight: 700, padding: '0 8px', color: 'var(--text-main)' }}>
                    Page {currentSafeUserPage} of {totalUserPages}
                  </span>
                  <button
                    disabled={currentSafeUserPage >= totalUserPages}
                    onClick={() => setUserPage(p => Math.min(totalUserPages, p + 1))}
                    className="btn btn-secondary"
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      borderRadius: '8px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      opacity: currentSafeUserPage >= totalUserPages ? 0.4 : 1,
                      cursor: currentSafeUserPage >= totalUserPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'reports' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', borderRadius: '20px' }}>
          <table className="admin-responsive-table admin-reports-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
            <thead>
              <tr style={{ background: 'var(--light-bg)', borderBottom: '1px solid var(--border)', textAlign: 'left', height: '52px' }}>
                <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Reported User</th>
                <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Reason</th>
                <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px' }}>Status</th>
                <th style={{ padding: '12px 18px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.8px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 && (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '14px' }}>No reports found.</td></tr>
              )}
              {reports.map(report => (
                <tr key={report.id} style={{ borderBottom: '1px solid var(--border)' }} className="table-row admin-report-row">
                  <td data-label="Reported User" style={{ padding: '14px 18px', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    <div style={{ fontWeight: 800, fontSize: '15px', color: 'var(--text-main)' }}>{report.reportedName || 'Unknown User'}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', wordBreak: 'break-all' }}>UID: {report.reportedUid || 'N/A'}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      Reported by: {report.reporterUid ? getReporterName(report.reporterUid) : 'Anonymous'}
                    </div>
                  </td>
                  <td data-label="Reason" style={{ padding: '14px 18px', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.5, color: 'var(--text-main)', fontStyle: 'italic' }}>"{report.reason}"</p>
                    {report.timestamp && (
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                        {new Date(report.timestamp).toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td data-label="Status" style={{ padding: '14px 18px' }} onClick={() => setSelectedReport(report)}>
                    {report.status === 'resolved' ? (
                      <span style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '8px', background: 'rgba(16, 185, 129, 0.1)', color: 'var(--secondary)', fontWeight: 800, textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <CheckCircle size={12} /> Resolved
                      </span>
                    ) : (
                      <span style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.1)', color: '#EF4444', fontWeight: 800, textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <AlertTriangle size={12} /> Pending
                      </span>
                    )}
                  </td>
                  <td data-label="Actions" className="admin-actions-cell" style={{ padding: '14px 18px', textAlign: 'right' }}>
                    <button
                      onClick={() => setSelectedReport(report)}
                      className="btn btn-secondary"
                      style={{ padding: '8px 14px', fontSize: '12px', borderRadius: '10px' }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'partners' && (
        <>
          <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '20px' }}>
            <div>
              <h3 style={{ margin: '0 0 6px 0', fontSize: '20px', fontWeight: 800 }}>Registered Resort & Hotel Partners</h3>
              <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '14px' }}>Click any resort to inspect its profile, amenities, rooms, and contact details.</p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ padding: '8px 16px', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.1)', color: '#2563EB', fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building2 size={16} /> {properties.length} Active Listings
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
            {properties.map(prop => {
              const imgs = Array.isArray(prop.imageUrls) ? prop.imageUrls : (prop.imageUrls ? Object.values(prop.imageUrls) : []);
              const banner = imgs[0] || 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=800&q=80';
              const roomsCount = prop.roomInventory ? Object.keys(prop.roomInventory).length : (prop.rooms || 0);
              const activitiesCount = prop.activities ? Object.keys(prop.activities).length : 0;
              
              return (
                <div 
                  key={prop.id} 
                  className="card" 
                  onClick={() => setSelectedProperty(prop)}
                  style={{
                    padding: 0,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid var(--border)',
                    boxShadow: 'var(--shadow)',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 16px 32px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = 'var(--shadow)';
                  }}
                >
                  <div style={{ position: 'relative', height: '170px', width: '100%', overflow: 'hidden', background: '#0F172A' }}>
                    <img 
                      src={banner} 
                      alt={prop.name} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                    <div style={{ position: 'absolute', top: '12px', left: '12px', background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', color: 'white', padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {prop.type || 'Resort'}
                    </div>
                    {prop.location && (
                      <div style={{ position: 'absolute', bottom: '12px', left: '12px', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', color: 'white', padding: '4px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={13} color="var(--secondary)" /> {prop.location}
                      </div>
                    )}
                  </div>

                  <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <h4 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: 'var(--text-main)' }}>{prop.name}</h4>
                    </div>

                    <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {prop.description || 'Verified partner establishment on Resort Connect.'}
                    </p>

                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: 'auto', marginBottom: '16px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '8px', background: 'var(--light-bg)', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <Bed size={13} /> {roomsCount} {roomsCount === 1 ? 'Room' : 'Rooms'}
                      </span>
                      {activitiesCount > 0 && (
                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '8px', background: 'var(--light-bg)', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                          <Activity size={13} /> {activitiesCount} Activities
                        </span>
                      )}
                      {prop.contactPhone && (
                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '8px', background: 'var(--light-bg)', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                          <Phone size={13} /> {prop.contactPhone}
                        </span>
                      )}
                    </div>

                    <button 
                      className="btn btn-secondary" 
                      style={{ width: '100%', padding: '10px', fontSize: '13px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProperty(prop);
                      }}
                    >
                      <Eye size={15} /> View Partner Overview
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {activeTab === 'cms' && <AdminCMS />}
      {activeTab === 'import' && <HistoricalDataImport profile={profile} uid={uid} onBack={() => setActiveTab('users')} />}

      {selectedUser && (
        <div className="modal-overlay" style={{ zIndex: 2500 }}>
          <div className="modal-content" style={{
            background: 'var(--surface)', borderRadius: '24px', maxWidth: '480px',
            width: '90%', padding: '32px 28px', position: 'relative',
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <button
              onClick={() => setSelectedUser(null)}
              style={{
                position: 'absolute', top: '16px', right: '16px',
                background: 'var(--light-bg)', color: 'var(--text-main)',
                border: '1px solid var(--border)', borderRadius: '50%',
                width: '32px', height: '32px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'var(--transition)'
              }}
              title="Close"
              aria-label="Close"
            >
              <X size={18} color="var(--text-main)" />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                width: '60px', height: '60px', borderRadius: '16px', background: 'rgba(59, 130, 246, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                color: '#1D4ED8', fontSize: '24px', fontWeight: 700, overflow: 'hidden'
              }}>
                {selectedUser.profilePicUrl ? (
                  <img src={selectedUser.profilePicUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  selectedUser.firstName?.charAt(0) || 'U'
                )}
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>{selectedUser.firstName} {selectedUser.lastName}</h3>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 500, marginTop: '2px' }}>
                  {selectedUser.email}
                </div>
                <span style={{
                  display: 'inline-block', fontSize: '11px', padding: '4px 8px', borderRadius: '6px',
                  background: selectedUser.role === 'Owner' ? 'rgba(16, 185, 129, 0.1)' : 'var(--light-bg)',
                  color: selectedUser.role === 'Owner' ? 'var(--secondary)' : 'var(--text-muted)',
                  fontWeight: 800, textTransform: 'uppercase', marginTop: '6px'
                }}>
                  {selectedUser.role || 'Tourist'}
                </span>
              </div>
            </div>

            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                ['Phone Number', selectedUser.phoneNumber || selectedUser.phone || 'N/A', <Phone size={16} />],
                ['Joined Date', selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : 'Unknown', <Calendar size={16} />],
                ['ID Type', selectedUser.idType || 'N/A', <Shield size={16} />],
                ['Verification', (selectedUser.identityStatus || (selectedUser.idVerified ? 'verified' : 'pending')).toUpperCase(), <ShieldCheck size={16} />],
                ['Warnings', `${selectedUser.warningCount || 0} / 3`, <AlertTriangle size={16} />]
              ].map(([label, val, icon]) => (
                <div key={label} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px', border: '1px solid var(--border)', borderRadius: '12px'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>
                    {icon} {label}
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)' }}>{val}</span>
                </div>
              ))}
            </div>

            {/* Verification Documents: Valid ID & Scanned Face (Selfie) */}
            <div style={{ marginTop: '20px' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '10px' }}>
                Identity Documents
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '10px', background: 'var(--light-bg)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '6px' }}>Uploaded ID</div>
                  {(selectedUser.idImageUrl || selectedUser.idPhotoUrl) ? (
                    <div
                      onClick={() => setPreviewImage({ url: selectedUser.idImageUrl || selectedUser.idPhotoUrl, title: `${selectedUser.firstName}'s Valid ID` })}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                    >
                      <img
                        src={selectedUser.idImageUrl || selectedUser.idPhotoUrl}
                        alt="Valid ID"
                        style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)' }}
                      />
                      <div style={{ fontSize: '11px', color: 'var(--primary)', marginTop: '4px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <Eye size={12} /> View Full ID
                      </div>
                    </div>
                  ) : (
                    <div style={{ height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic', background: 'var(--surface)', borderRadius: '8px' }}>
                      No ID Uploaded
                    </div>
                  )}
                </div>

                <div style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '10px', background: 'var(--light-bg)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '6px' }}>Scanned Face</div>
                  {(selectedUser.selfieUrl || selectedUser.selfieImageUrl) ? (
                    <div
                      onClick={() => setPreviewImage({ url: selectedUser.selfieUrl || selectedUser.selfieImageUrl, title: `${selectedUser.firstName}'s Scanned Face` })}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                    >
                      <img
                        src={selectedUser.selfieUrl || selectedUser.selfieImageUrl}
                        alt="Scanned Face"
                        style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)' }}
                      />
                      <div style={{ fontSize: '11px', color: 'var(--primary)', marginTop: '4px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <Eye size={12} /> View Full Face
                      </div>
                    </div>
                  ) : (
                    <div style={{ height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic', background: 'var(--surface)', borderRadius: '8px' }}>
                      No Face Scanned
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '24px' }}>
              <button
                className="btn btn-secondary"
                style={{ width: '100%', padding: '12px' }}
                onClick={() => setSelectedUser(null)}
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedProperty && (
        <div className="modal-overlay" style={{ zIndex: 2500 }}>
          <div className="modal-content" style={{
            background: 'var(--surface)', borderRadius: '24px', maxWidth: '620px',
            width: '92%', padding: '32px 28px', position: 'relative',
            boxShadow: '0 24px 64px rgba(0,0,0,0.22)', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <button
              onClick={() => setSelectedProperty(null)}
              style={{
                position: 'absolute', top: '16px', right: '16px',
                background: 'var(--light-bg)', color: 'var(--text-main)',
                border: '1px solid var(--border)', borderRadius: '50%',
                width: '32px', height: '32px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', zIndex: 2, transition: 'var(--transition)'
              }}
              title="Close"
              aria-label="Close"
            >
              <X size={18} color="var(--text-main)" />
            </button>

            {/* Gallery / Banner */}
            {(() => {
              const imgs = Array.isArray(selectedProperty.imageUrls) 
                ? selectedProperty.imageUrls 
                : (selectedProperty.imageUrls ? Object.values(selectedProperty.imageUrls) : []);
              const rooms = selectedProperty.roomInventory ? Object.values(selectedProperty.roomInventory) : [];
              const activities = selectedProperty.activities ? Object.values(selectedProperty.activities) : [];

              return (
                <div>
                  <div style={{ position: 'relative', height: '200px', borderRadius: '18px', overflow: 'hidden', marginBottom: '20px', background: '#0F172A' }}>
                    <img 
                      src={imgs[0] || 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=800&q=80'} 
                      alt="" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                    <div style={{ position: 'absolute', top: '12px', left: '12px', background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', color: 'white', padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase' }}>
                      {selectedProperty.type || 'Resort'} Partner
                    </div>
                  </div>

                  <h3 style={{ margin: '0 0 6px 0', fontSize: '24px', fontWeight: 900, color: 'var(--text-main)' }}>
                    {selectedProperty.name}
                  </h3>

                  {selectedProperty.location && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: '14px', marginBottom: '14px' }}>
                      <MapPin size={15} color="var(--secondary)" /> {selectedProperty.location}
                    </div>
                  )}

                  <p style={{ margin: '0 0 20px 0', fontSize: '14px', lineHeight: 1.6, color: 'var(--text-main)', opacity: 0.9 }}>
                    {selectedProperty.description || 'No description provided.'}
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ padding: '12px 14px', background: 'var(--light-bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Rooms Listed</div>
                      <div style={{ fontSize: '18px', fontWeight: 800, marginTop: '2px', color: 'var(--text-main)' }}>{rooms.length}</div>
                    </div>
                    <div style={{ padding: '12px 14px', background: 'var(--light-bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Activities</div>
                      <div style={{ fontSize: '18px', fontWeight: 800, marginTop: '2px', color: 'var(--text-main)' }}>{activities.length}</div>
                    </div>
                    <div style={{ padding: '12px 14px', background: 'var(--light-bg)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Staff Count</div>
                      <div style={{ fontSize: '18px', fontWeight: 800, marginTop: '2px', color: 'var(--text-main)' }}>{selectedProperty.staffCount || 'N/A'}</div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: '10px', marginBottom: '20px' }}>
                    {[
                      ['Contact Email', selectedProperty.contactEmail || 'N/A', <Mail size={15} />],
                      ['Contact Phone', selectedProperty.contactPhone || 'N/A', <Phone size={15} />],
                      ['Check-in / Check-out', `${selectedProperty.checkInTime || '2:00 PM'} / ${selectedProperty.checkOutTime || '12:00 NN'}`, <Calendar size={15} />],
                      ['GCash Payment', selectedProperty.gcashNumber ? `${selectedProperty.gcashName || 'Partner'} (${selectedProperty.gcashNumber})` : 'Not configured', <Building2 size={15} />],
                    ].map(([label, val, icon]) => (
                      <div key={label} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 14px', border: '1px solid var(--border)', borderRadius: '12px'
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>
                          {icon} {label}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-main)' }}>{val}</span>
                      </div>
                    ))}
                  </div>

                  {rooms.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: '10px' }}>Room Inventory Preview</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {rooms.map((rm, i) => (
                          <div key={i} style={{ flex: '1 1 130px', padding: '10px 12px', background: 'var(--light-bg)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                            <div style={{ fontWeight: 800, fontSize: '13px', color: 'var(--text-main)', wordBreak: 'break-word' }}>{rm.title}</div>
                            <div style={{ fontSize: '12px', color: 'var(--primary)', fontWeight: 700 }}>₱{rm.price}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Max {rm.maxPax || 2} Pax</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      className="btn btn-secondary"
                      style={{ flex: 1, padding: '12px' }}
                      onClick={() => setSelectedProperty(null)}
                    >
                      Close Overview
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {selectedReport && !chatOpen && (
        <div className="modal-overlay" style={{ zIndex: 2500 }}>
          <div className="modal-content" style={{
            background: 'var(--surface)', borderRadius: '24px', maxWidth: '480px',
            width: '90%', padding: '32px 28px', position: 'relative',
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)'
          }}>
            <button onClick={() => { setSelectedReport(null); setChatMessages([]); }} style={{
              position: 'absolute', top: '16px', right: '16px', background: 'var(--light-bg)',
              border: 'none', borderRadius: '50%', width: '32px', height: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
            }}>
              <X size={16} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(239, 68, 68, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <AlertTriangle size={24} color="#EF4444" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>Report Details</h3>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {selectedReport.timestamp ? new Date(selectedReport.timestamp).toLocaleString() : 'Unknown date'}
                </div>
              </div>
            </div>

            {[
              ['Reported User', selectedReport.reportedName || selectedReport.reportedUid],
              ['Reporter', getReporterName(selectedReport.reporterUid)],
              ['Status', selectedReport.status],
            ].map(([label, val]) => (
              <div key={label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: '1px solid var(--border)'
              }}>
                <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 700 }}>{label}</span>
                <span style={{ fontSize: '13px', fontWeight: 600, textTransform: label === 'Status' ? 'capitalize' : 'none' }}>{val}</span>
              </div>
            ))}

            <div style={{
              background: 'var(--light-bg)', borderRadius: '12px', padding: '14px 16px',
              marginTop: '16px', marginBottom: '20px'
            }}>
              <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '6px' }}>Reason</div>
              <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, fontStyle: 'italic', color: 'var(--text-main)' }}>"{selectedReport.reason}"</p>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
              <button
                className="btn btn-primary"
                style={{ width: '100%', padding: '12px' }}
                onClick={() => setChatOpen(true)}
              >
                View Chat History
              </button>
              {selectedReport.status === 'pending' && (
                <button
                  className="btn"
                  style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white' }}
                  onClick={() => { openResolveModal(selectedReport); setSelectedReport(null); }}
                >
                  Mark as Resolved
                </button>
              )}
              <button
                className="btn btn-secondary"
                style={{ width: '100%', padding: '12px' }}
                onClick={() => { setSelectedReport(null); setChatMessages([]); }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedReport && chatOpen && (
        <div className="modal-overlay" style={{ zIndex: 2600, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}>
          <div className="modal-content" style={{
            background: 'var(--surface)', borderRadius: '28px', maxWidth: '600px',
            width: '95%', height: '85vh', display: 'flex', flexDirection: 'column',
            padding: '0', position: 'relative', overflow: 'hidden',
            boxShadow: '0 32px 80px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--surface)',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <button onClick={() => setChatOpen(false)} style={{
                  background: 'var(--light-bg)', border: '1px solid var(--border)', width: '36px', height: '36px',
                  borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'var(--text-main)'
                }}>
                  <ArrowLeft size={20} />
                </button>

                <div style={{ position: 'relative' }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '16px',
                    background: 'var(--light-bg)',
                    overflow: 'hidden',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    color: 'var(--text-muted)', border: '2px solid var(--border)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }}>
                    {reportedPhoto ? (
                      <img src={reportedPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <User size={24} />
                    )}
                  </div>
                  <div style={{
                    position: 'absolute', bottom: '-2px', right: '-2px',
                    width: '14px', height: '14px', background: '#10B981',
                    borderRadius: '50%', border: '2px solid white'
                  }}></div>
                </div>

                <div>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {selectedReport.reportedName || 'Reported User'}
                    <ShieldCheck size={14} color="var(--secondary)" />
                  </h4>
                  <span style={{ fontSize: '12px', color: 'var(--secondary)', fontWeight: 700 }}>Online</span>
                </div>
              </div>
            </div>

            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              background: 'var(--light-bg)'
            }}>
              {chatLoading ? (
                <div style={{ textAlign: 'center', margin: 'auto', color: 'var(--text-muted)' }}>Loading chat transcript...</div>
              ) : chatMessages.length === 0 ? (
                <div style={{ textAlign: 'center', margin: 'auto', opacity: 0.5 }}>
                  <div style={{ background: 'var(--surface)', padding: '20px', borderRadius: '24px', display: 'inline-block', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', border: '1px solid var(--border)' }}>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: 600 }}>No chat messages found between these parties.</p>
                  </div>
                </div>
              ) : (
                chatMessages.map((msg) => {
                  const isReporter = msg.senderUid === selectedReport.reporterUid;
                  const showTime = true;

                  const formatMessageTime = (ts) => {
                    if (!ts) return '';
                    const d = new Date(ts);
                    if (isToday(d)) return format(d, 'p');
                    if (isThisYear(d)) return format(d, 'MMM d, p');
                    return format(d, 'MMM d, yyyy, p');
                  };

                  return (
                    <div key={msg.id} style={{
                      alignSelf: isReporter ? 'flex-start' : 'flex-end',
                      maxWidth: '85%',
                      display: 'flex',
                      gap: '12px',
                      flexDirection: isReporter ? 'row' : 'row-reverse',
                      alignItems: 'flex-end'
                    }}>
                      <div style={{
                        width: '32px', height: '32px', borderRadius: '10px',
                        background: 'var(--light-bg)', overflow: 'hidden',
                        flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.05)', marginBottom: showTime ? '18px' : '0'
                      }}>
                        {isReporter ? (
                          reporterPhoto ? <img src={reporterPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <User size={16} color="#9CA3AF" />
                        ) : (
                          reportedPhoto ? <img src={reportedPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <User size={16} color="#9CA3AF" />
                        )}
                      </div>

                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isReporter ? 'flex-start' : 'flex-end'
                      }}>
                        <div style={{
                          padding: '12px 18px',
                          borderRadius: isReporter ? '20px 20px 20px 4px' : '20px 20px 4px 20px',
                          background: isReporter ? 'var(--surface)' : 'linear-gradient(135deg, var(--primary), #FF5F6D)',
                          color: isReporter ? 'var(--text-main)' : 'white',
                          fontSize: '15px',
                          fontWeight: 500,
                          boxShadow: isReporter ? '0 2px 8px rgba(0,0,0,0.03)' : '0 4px 15px rgba(251, 54, 64, 0.2)',
                          lineHeight: '1.5',
                          border: isReporter ? '1px solid var(--border)' : 'none',
                          wordBreak: 'break-word'
                        }}>
                          {msg.decryptedText || msg.text || ''}
                        </div>
                        {showTime && msg.timestamp && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              {formatMessageTime(msg.timestamp)}
                            </span>
                            {!isReporter && (
                              <CheckCheck size={14} color="#3B82F6" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ padding: '20px 24px', background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    disabled
                    placeholder="Read-only monitor mode... cannot send messages."
                    style={{
                      width: '100%', padding: '14px 20px', borderRadius: '18px', border: '2px solid var(--border)',
                      background: 'var(--light-bg)', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: '15px',
                      fontWeight: 500, cursor: 'not-allowed'
                    }}
                  />
                </div>
                <button
                  disabled
                  style={{
                    width: '52px', height: '52px', borderRadius: '18px', border: '1px solid var(--border)',
                    background: 'var(--light-bg)', color: 'var(--text-muted)', display: 'flex', justifyContent: 'center',
                    alignItems: 'center', cursor: 'not-allowed'
                  }}
                >
                  <Send size={22} />
                </button>
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0 }}>
              <button
                className="btn btn-secondary"
                style={{ width: '100%', padding: '12px', borderRadius: '14px', fontWeight: 700 }}
                onClick={() => setChatOpen(false)}
              >
                ← Back to Report Details
              </button>
            </div>
          </div>
        </div>
      )}

      {banModal && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ background: 'var(--surface)', borderRadius: '24px', maxWidth: '420px', width: '90%', padding: '32px 28px', textAlign: 'center' }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px',
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              background: banModal.isBanned ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'
            }}>
              {banModal.isBanned ? <UserCheck size={32} color="#10B981" /> : <UserX size={32} color="#EF4444" />}
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 800 }}>
              {banModal.isBanned ? 'Unban Account?' : 'Restrict Access?'}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>
              {banModal.isBanned
                ? `Are you sure you want to restore access for ${banModal.firstName || 'this user'}? They will be able to use the platform again.`
                : `Are you sure you want to restrict ${banModal.firstName || 'this user'}? They will not be able to log in or use the platform.`
              }
            </p>
            {!banModal.isBanned && (
              <div style={{ textAlign: 'left', marginBottom: '20px' }}>
                <label style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px' }}>
                  Reason for Restriction *
                </label>
                <textarea
                  className="input"
                  rows={3}
                  placeholder="e.g., Violation of terms of service, inappropriate conduct..."
                  value={banReason}
                  onChange={(e) => { setBanReason(e.target.value); if (banError) setBanError(''); }}
                  style={{ width: '100%', resize: 'none' }}
                />
              </div>
            )}
            {banError && (
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#B91C1C', padding: '10px 14px', borderRadius: '12px', fontSize: '13px', fontWeight: 700, marginBottom: '16px', border: '1px solid #FEE2E2' }}>
                {banError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn" style={{ flex: 1, background: 'var(--light-bg)', color: 'var(--text-main)', border: '1px solid var(--border)' }} onClick={() => { setBanModal(null); setBanReason(''); setBanError(''); }}>
                Cancel
              </button>
              <button
                className="btn"
                disabled={banLoading}
                style={{
                  flex: 1,
                  background: banModal.isBanned ? 'linear-gradient(135deg, #10B981, #059669)' : 'linear-gradient(135deg, #EF4444, #DC2626)',
                  color: 'white', opacity: banLoading ? 0.7 : 1
                }}
                onClick={confirmBan}
              >
                {banLoading ? 'Processing...' : (banModal.isBanned ? 'Unban Account' : 'Restrict Access')}
              </button>
            </div>
            <button
              onClick={() => { setBanModal(null); setBanReason(''); setBanError(''); }}
              style={{
                position: 'absolute', top: '16px', right: '16px',
                background: 'var(--light-bg)', color: 'var(--text-main)',
                border: '1px solid var(--border)', borderRadius: '50%',
                width: '32px', height: '32px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'var(--transition)'
              }}
              title="Close"
              aria-label="Close"
            >
              <X size={18} color="var(--text-main)" />
            </button>
          </div>
        </div>
      )}

      {resolveModal && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ background: 'var(--surface)', borderRadius: '24px', maxWidth: '400px', width: '90%', padding: '32px 28px', textAlign: 'center' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(16, 185, 129, 0.1)' }}>
              <CheckCircle size={32} color="#10B981" />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 800 }}>Resolve Report Action</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '16px' }}>
              Select an action to take before resolving this report.
            </p>
            <div style={{ background: 'var(--light-bg)', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', textAlign: 'left' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '4px' }}>Report against: <span style={{ color: 'var(--text-main)' }}>{resolveModal.reportedName || resolveModal.reportedUid}</span></div>
              <div style={{ fontSize: '13px', color: 'var(--text-main)', fontStyle: 'italic' }}>"{resolveModal.reason}"</div>
            </div>

            <div style={{ textAlign: 'left', marginBottom: '24px' }}>
              <select className="input" value={resolveAction} onChange={(e) => setResolveAction(e.target.value)} style={{ width: '100%', marginBottom: '12px' }}>
                <option value="dismiss">Dismiss / No Action</option>
                <option value="warn_reported">Warn Reported User</option>
                <option value="ban_reported">Ban Reported User</option>
                <option value="warn_reporter">Warn Reporter (False Report)</option>
              </select>

              {resolveAction !== 'dismiss' && (
                <textarea 
                  className="input" 
                  placeholder={resolveAction === 'ban_reported' ? "Reason for banning..." : "Message for warning..."}
                  rows={3} 
                  style={{ width: '100%', resize: 'none' }}
                  value={resolveMessage}
                  onChange={(e) => setResolveMessage(e.target.value)}
                />
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn" style={{ flex: 1, background: 'var(--light-bg)', color: 'var(--text-main)', border: '1px solid var(--border)' }} onClick={() => setResolveModal(null)}>Cancel</button>
              <button className="btn" disabled={resolveLoading} style={{ flex: 1, background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', opacity: resolveLoading ? 0.7 : 1 }} onClick={confirmResolve}>
                {resolveLoading ? 'Processing...' : 'Confirm Action'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div
          className="modal-overlay"
          onClick={() => setPreviewImage(null)}
          style={{ zIndex: 9999, background: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: '20px',
              maxWidth: '650px',
              width: '100%',
              padding: '24px',
              position: 'relative',
              boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
              textAlign: 'center'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
              <h4 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{previewImage.title || 'Document Preview'}</h4>
              <button
                onClick={() => setPreviewImage(null)}
                style={{
                  background: 'var(--light-bg)',
                  color: 'var(--text-main)',
                  border: '1px solid var(--border)',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'var(--transition)'
                }}
                title="Close"
                aria-label="Close"
              >
                <X size={18} color="var(--text-main)" />
              </button>
            </div>
            <div style={{ background: '#000', borderRadius: '12px', overflow: 'hidden', maxHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={previewImage.url}
                alt={previewImage.title || 'Preview'}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setPreviewImage(null)}
                style={{ padding: '8px 20px', borderRadius: '10px' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .table-row:hover { background: var(--card-hover-bg); }
        .view-transition { animation: fadeIn 0.4s ease-out; }
        .stat-card { transition: transform 0.2s, box-shadow 0.2s; }
        .stat-card[style*="cursor: pointer"]:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(0,0,0,0.1); }

        .admin-hero-banner {
          transition: padding 0.3s ease;
        }

        .admin-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .admin-tabs-nav {
          overflow-x: auto;
          white-space: nowrap;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .admin-tabs-nav::-webkit-scrollbar {
          display: none;
        }

        @media (max-width: 1024px) {
          .admin-stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)) !important;
            gap: 12px !important;
          }
          .admin-hero-banner {
            padding: 20px 18px !important;
            margin-bottom: 16px !important;
          }
          .admin-hero-title {
            font-size: 22px !important;
          }
        }

        @media (max-width: 860px) {
          .admin-stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)) !important;
            gap: 10px !important;
          }
          .admin-stats-grid .stat-card {
            padding: 16px !important;
          }
          .admin-stats-grid .stat-card > div:nth-child(2) {
            font-size: 24px !important;
          }

          .admin-filter-bar {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .admin-filter-controls {
            width: 100% !important;
            justify-content: stretch !important;
          }
          .admin-filter-controls > div {
            flex: 1 1 100% !important;
          }
          .admin-filter-controls > select {
            flex: 1 1 100% !important;
          }

          .admin-user-type-badge {
            display: inline-block !important;
          }

          .admin-responsive-table thead {
            display: none !important;
          }
          .admin-responsive-table, 
          .admin-responsive-table tbody, 
          .admin-responsive-table tr, 
          .admin-responsive-table td {
            display: block !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .admin-responsive-table tr {
            padding: 14px 14px 12px 14px !important;
            border-bottom: 1px solid var(--border) !important;
            position: relative;
            background: transparent !important;
          }
          .admin-responsive-table tr:last-child {
            border-bottom: none !important;
          }
          .admin-responsive-table td {
            padding: 3px 0 !important;
            border: none !important;
            text-align: left !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 2px !important;
          }
          .admin-responsive-table td.admin-cell-type {
            display: none !important;
          }
          .admin-responsive-table td::before {
            content: attr(data-label);
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.6px;
            color: var(--text-muted);
            margin-bottom: 1px;
          }
          .admin-responsive-table td.admin-cell-account::before {
            display: none !important;
          }
          .admin-responsive-table td.admin-actions-cell {
            padding-top: 10px !important;
            border-top: 1px dashed var(--border) !important;
            margin-top: 6px;
          }
          .admin-responsive-table td.admin-actions-cell::before {
            display: none !important;
          }
          .admin-responsive-table td.admin-actions-cell > div {
            justify-content: flex-start !important;
            gap: 8px !important;
          }
        }

        @media (min-width: 861px) {
          .admin-user-type-badge {
            display: none !important;
          }
        }

        @media (max-width: 520px) {
          .admin-hero-banner {
            padding: 14px 12px !important;
            margin-bottom: 12px !important;
          }
          .admin-hero-title {
            font-size: 17px !important;
          }
          .admin-hero-subtitle {
            font-size: 11.5px !important;
          }
          .admin-stats-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 8px !important;
            margin-bottom: 14px !important;
          }
          .admin-stats-grid .stat-card {
            padding: 10px 8px !important;
            border-radius: 12px !important;
          }
          .admin-stats-grid .stat-card > div:nth-child(1) {
            padding: 8px !important;
            border-radius: 10px !important;
            margin-bottom: 6px !important;
          }
          .admin-stats-grid .stat-card > div:nth-child(1) svg {
            width: 16px !important;
            height: 16px !important;
          }
          .admin-stats-grid .stat-card > div:nth-child(2) {
            font-size: 18px !important;
          }
          .admin-stats-grid .stat-card > div:nth-child(3) {
            font-size: 9.5px !important;
            letter-spacing: 0.2px !important;
          }
          .admin-responsive-table tr {
            padding: 10px 8px !important;
          }
          .admin-responsive-table td.admin-actions-cell > div {
            flex-direction: row !important;
            gap: 6px !important;
          }
          .admin-responsive-table td.admin-actions-cell > div button {
            flex: 1 1 0 !important;
            min-width: 0 !important;
            padding: 7px 4px !important;
            font-size: 11px !important;
            justify-content: center !important;
            text-align: center !important;
          }
        }
      `}</style>
    </div>
  );
};

const StatItem = ({ icon, label, value, bgGradient, onClick }) => (
  <div className="card stat-card" onClick={onClick} style={{ margin: 0, padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', background: 'var(--surface)', cursor: onClick ? 'pointer' : 'default' }}>
    <div style={{ background: bgGradient, padding: '14px', borderRadius: '18px', display: 'inline-flex', marginBottom: '14px' }}>{icon}</div>
    <div style={{ fontSize: '32px', fontWeight: 900, letterSpacing: '-1px' }}>{value}</div>
    <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{label}</div>
  </div>
);

export default AdminDashboard;
